import React from 'react';
import { ScheduledReportProvider, useFields, getTimeFromModel } from './State';

// Prioritized American timezones at the top
const PRIORITY_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const PRIORITY_TIMEZONE_LABELS = {
  'America/New_York': 'Eastern Time (ET)',
  'America/Chicago': 'Central Time (CT)',
  'America/Denver': 'Mountain Time (MT)',
  'America/Los_Angeles': 'Pacific Time (PT)',
  'America/Phoenix': 'Arizona (MT - No DST)',
  'America/Anchorage': 'Alaska Time (AKT)',
  'Pacific/Honolulu': 'Hawaii Time (HT)',
};

export default (props) => {
  const {
    model,
    modelName = 'scheduled_report',
    frequencyOptions,
    timezoneOptions,
    errors,
    funds,
  } = props;

  // Filter out priority timezones from the main list to avoid duplicates
  const otherTimezones = timezoneOptions.filter(tz => !PRIORITY_TIMEZONES.includes(tz));

  return (
    <ScheduledReportProvider
      model={model}
      modelName={modelName}
      errors={errors}
    >
      <div className="space-y-6">
        {!!model.id && (
          <input type="hidden" name={`${modelName}[id]`} value={model.id} />
        )}

        <TimeField />
        <FrequencyField frequencyOptions={frequencyOptions} />
        <DayField />
        <TimezoneField timezoneOptions={otherTimezones} />
        <FundField funds={funds} />
        <EmailField />
      </div>
    </ScheduledReportProvider>
  );
};

const TimeField = () => {
  const { state, setTime } = useFields();
  const { model, modelName, hour, minute, ampm } = state;

  const onChangeTime = (e) => {
    const { name, value } = e.target;
    setTime(value, name);
  };

  const selectClassName = "block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm";

  return (
    <div>
      <input
        type="hidden"
        name={`${modelName}[time]`}
        value={`${hour}:${minute} ${ampm}`}
      />

      <label className="block text-sm font-medium text-gray-700 mb-2">
        Time
      </label>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <select
            id="hour-select"
            name="hour"
            defaultValue={getTimeFromModel('hour', model)}
            onChange={onChangeTime}
            className={selectClassName}
          >
            {new Array(12).fill(null).map((v, i) => {
              return (
                <option key={i} value={i + 1}>
                  {i + 1}
                </option>
              );
            })}
          </select>
        </div>
        <span className="text-gray-500 font-medium">:</span>
        <div className="flex-1">
          <select
            id="minute-select"
            name="minute"
            defaultValue={getTimeFromModel('minute', model)}
            onChange={onChangeTime}
            className={selectClassName}
          >
            {new Array(12).fill(null).map((v, i) => {
              const value = `${i * 5}`.length < 2 ? `0${i * 5}` : `${i * 5}`;
              return (
                <option key={i} value={value}>
                  {value}
                </option>
              );
            })}
          </select>
        </div>
        <div className="flex-1">
          <select
            id="ampm-select"
            name="ampm"
            defaultValue={getTimeFromModel('ampm', model)}
            onChange={onChangeTime}
            className={selectClassName}
          >
            {['AM', 'PM'].map((v, i) => {
              return (
                <option key={i} value={v}>
                  {v}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <p className="mt-1.5 text-xs text-gray-500">Select the hour, minute, and AM/PM for report delivery</p>
    </div>
  );
};

const FrequencyField = (props) => {
  const { state, setFrequency } = useFields();
  const { model, modelName } = state;
  const { frequencyOptions } = props;

  const onChangeFrequency = (e) => {
    const { value } = e.target;
    setFrequency(value);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Frequency
      </label>
      <select
        id="frequency-select"
        name={`${modelName}[frequency]`}
        defaultValue={model.frequency}
        onChange={onChangeFrequency}
        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
      >
        {frequencyOptions.map((v, i) => {
          return (
            <option key={i} value={v}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </option>
          );
        })}
      </select>
      <p className="mt-1.5 text-xs text-gray-500">How often should this report be generated</p>
    </div>
  );
};

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const DayField = () => {
  const { state, setDay } = useFields();
  const { model, modelName } = state;

  const onChangeDay = (e) => {
    const { value } = e.target;
    setDay(value);
  };

  if (state.frequency === 'daily') return null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Day of Week
      </label>
      <select
        id="day-select"
        name={`${modelName}[day]`}
        defaultValue={model.day}
        onChange={onChangeDay}
        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
      >
        {DAYS.map((v, i) => {
          return (
            <option key={i} value={v}>
              {v}
            </option>
          );
        })}
      </select>
      <p className="mt-1.5 text-xs text-gray-500">Which day of the week to send the report</p>
    </div>
  );
};

const TimezoneField = (props) => {
  const { state, setTimeZone } = useFields();
  const { model, modelName } = state;
  const { timezoneOptions } = props;

  const onChangeTimezone = (e) => {
    const { value } = e.target;
    setTimeZone(value);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Timezone
      </label>
      <select
        id="timezone-select"
        name={`${modelName}[timezone]`}
        defaultValue={model.timezone}
        onChange={onChangeTimezone}
        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
      >
        <optgroup label="United States">
          {PRIORITY_TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {PRIORITY_TIMEZONE_LABELS[tz]}
            </option>
          ))}
        </optgroup>
        <optgroup label="All Timezones">
          {timezoneOptions.map((v, i) => {
            return (
              <option key={i} value={v}>
                {v.replace(/_/g, ' ')}
              </option>
            );
          })}
        </optgroup>
      </select>
      <p className="mt-1.5 text-xs text-gray-500">The timezone used to determine when to send the report</p>
    </div>
  );
};

const EmailField = () => {
  const { state, setEmail } = useFields();
  const { model, modelName, errors } = state;

  const onChangeEmail = (e) => {
    const { value } = e.target;
    setEmail(value);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Email Address
      </label>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <input
          type="email"
          id="email-input"
          name={`${modelName}[email]`}
          defaultValue={model.email}
          onChange={onChangeEmail}
          placeholder="reports@example.com"
          className={`block w-full rounded-lg border-gray-300 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
        />
      </div>
      {!!errors.email && (
        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {errors.email}
        </p>
      )}
      <p className="mt-1.5 text-xs text-gray-500">The email address where the report will be sent</p>
    </div>
  );
};

const FundField = (props) => {
  const { state } = useFields();
  const { model, modelName } = state;
  const { funds } = props;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Report Scope
        <span className="ml-1 text-xs font-normal text-gray-500">(Optional)</span>
      </label>
      <select
        id="fund-select"
        name={`${modelName}[fund_id]`}
        defaultValue={model.fund_id || ''}
        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
      >
        <option value="">All Funds</option>
        {funds && funds.map((fund) => (
          <option key={fund.id} value={fund.id}>
            {fund.name}
          </option>
        ))}
      </select>
      <p className="mt-1.5 text-xs text-gray-500">Leave as "All Funds" to include transactions from all funds, or select a specific fund</p>
    </div>
  );
};
